import { IStartProcessTrigger, IStartProcessTriggerSourceType } from "@kuika/kuika-cl-model";

/**
 * The landing's "Flow" column: how a workflow gets started, plus how big its diagram is —
 * both derived from data the page already has loaded, no new backend call.
 *
 * `json` is `IWorkFlowDto.json` verbatim: `JSON.stringify({ parameters, nodes,
 * happyPathDefinition })`, written by `workflow-provider.tsx`'s `convertWFToWfDto` and read back
 * by `convertWFDtoToWf` — see `kuika-cl-model/src/models/workflow.ts`'s `IWFNodes` (`steps`,
 * `events`, `gateways`, `forwards`, `decisions`, `lanes`). A BPMN start event there
 * (`nodes.events[].eventType === EWFEventType.Start`) is generic — it does not itself record
 * WHAT starts the workflow. That answer lives on the PROJECT, not the workflow: `IProject.
 * startProcessTriggers` is a reverse index (`Record<workflowId, IStartProcessTrigger[]>`) built
 * from every Screen/ScheduledJob/ApiManagement action that calls `StartProcess` against this
 * workflow (`start-process-trigger-index-helper.ts`) — already loaded alongside every other
 * workflow when the project itself is, so still "data the page already has", just not inside
 * this one row's own `json`.
 */

export interface IWorkflowListSummary {
  /** How this workflow gets started, in the product's own vocabulary (`IStartProcessTriggerSourceType`).
   *  null when nothing in the project currently starts it. */
  triggerKind: string | null;
  /** Steps in the diagram (`nodes.steps` — `ScreenInteraction`/`BackendInteraction`/`Subprocess`/
   *  `LinkedProcess` nodes a person actually authors), not gateways, arrows, lanes or events. */
  stepCount: number;
}

const TRIGGER_KIND_LABELS: Record<IStartProcessTriggerSourceType, string> = {
  Screen: "Screen",
  ScheduledJob: "Scheduled job",
  ApiManagement: "API"
};

/** The shape `JSON.parse(IWorkFlowDto.json)` actually carries, narrowed just far enough to count
 *  steps — everything else in `IWFNodes` is irrelevant to this column. */
interface IParsedWorkflowNodes {
  steps?: unknown[];
}

interface IParsedWorkflowJson {
  nodes?: IParsedWorkflowNodes;
}

/**
 * `nodes.steps.length`, parsed defensively: a legacy row, one saved before its first diagram
 * edit, or a malformed value all read as zero steps rather than throwing and blanking the row.
 */
export const countWorkflowSteps = (json: string | undefined): number => {
  if (!json) return 0;
  try {
    const parsed: IParsedWorkflowJson = JSON.parse(json) as IParsedWorkflowJson;
    const steps: unknown[] | undefined = parsed.nodes?.steps;
    return Array.isArray(steps) ? steps.length : 0;
  } catch {
    return 0;
  }
};

/**
 * The distinct source types that currently start this workflow, in the product's own labels —
 * joined when more than one wires into it (e.g. both a screen button and a scheduled job).
 */
export const describeWorkflowTriggerKind = (triggers: IStartProcessTrigger[] | undefined): string | null => {
  if (!triggers || triggers.length === 0) return null;
  const distinctSourceTypes: IStartProcessTriggerSourceType[] = Array.from(
    new Set(triggers.map((trigger: IStartProcessTrigger): IStartProcessTriggerSourceType => trigger.sourceType))
  );
  return distinctSourceTypes
    .map((sourceType: IStartProcessTriggerSourceType): string => TRIGGER_KIND_LABELS[sourceType])
    .join(" / ");
};

export const describeWorkflowListSummary = (
  json: string | undefined,
  triggers: IStartProcessTrigger[] | undefined
): IWorkflowListSummary => ({
  triggerKind: describeWorkflowTriggerKind(triggers),
  stepCount: countWorkflowSteps(json)
});

/**
 * The Flow cell's text: "Screen · 3 steps", or just "1 step" when nothing starts the workflow —
 * a dash before the separator read as a missing value rather than as "no trigger".
 */
export const formatWorkflowFlowCell = (summary: IWorkflowListSummary): string => {
  const steps: string = summary.stepCount === 1 ? "1 step" : `${summary.stepCount} steps`;
  return summary.triggerKind === null ? steps : `${summary.triggerKind} · ${steps}`;
};
